/**
 * Anchor resolution from the URL.
 *
 * Per-anchor URLs follow ``<slug>.pos.${env}.apps.myfinterra.com``
 * (slug as the leftmost label so a single wildcard cert covers all
 * anchors). The page reads the slug at startup and asks the POS API
 * for the matching anchor metadata.
 *
 * For dev / local /  un-anchored deploys the hostname is ``pos.${env}…``
 * with no leading slug - in that case the page falls back to a query-
 * param escape hatch (``?anchor=organization:<uuid>`` or
 * ``?anchor=partner:<uuid>``, or ``?slug=<slug>``) so we can dogfood
 * before per-anchor DNS records are wired. The kind prefix must be
 * the literal ``partner`` or ``organization`` - these match the
 * pos_user_tbl anchor_kind values.
 */

export interface AnchorHint {
  /** Slug from the hostname (leftmost label when followed by ``.pos.``). */
  readonly slug?: string;
  /** Direct anchor pointer from a query param (?anchor=kind:uuid). */
  readonly direct?: {
    readonly anchor_kind: "partner" | "organization";
    readonly anchor_id: string;
  };
}

/**
 * Read the anchor hint from ``window.location``. Returns ``{}`` when
 * no hint is present - caller renders a "no anchor selected" state
 * (login still works once the user manually picks one in the future
 * UI; right now there's nothing they can do, so the page should
 * surface a clear "this is the un-anchored URL - go to your branded
 * URL" message).
 */
export function readAnchorHint(loc: Location = window.location): AnchorHint {
  // Hostname → slug. Match ``<slug>.pos.<rest>``; slug = first label
  // ONLY when the second is literally ``pos``. ``pos.dev.apps...``
  // (no leading slug) returns hint.slug = undefined.
  let slug: string | undefined;
  const hostname = loc.hostname.toLowerCase();
  const labels = hostname.split(".");
  if (labels.length >= 2 && labels[1] === "pos") {
    slug = labels[0];
  }

  // Query-param escape hatches.
  const qs = new URLSearchParams(loc.search);
  const slugParam = qs.get("slug");
  if (slugParam && !slug) {
    slug = slugParam;
  }
  let direct: AnchorHint["direct"] | undefined;
  const anchorParam = qs.get("anchor");
  if (anchorParam) {
    const [kind, id] = anchorParam.split(":");
    if ((kind === "partner" || kind === "organization") && id) {
      direct = { anchor_kind: kind, anchor_id: id };
    }
  }

  // Build the result with only set keys - exactOptionalPropertyTypes
  // treats ``undefined`` differently from "key missing", so don't
  // assign undefined to optional fields. Typing the local as
  // ``AnchorHint`` (not via indexed access) keeps the readonly shape.
  const hint: AnchorHint = {};
  if (slug !== undefined) (hint as { slug?: string }).slug = slug;
  if (direct !== undefined) {
    (hint as { direct?: AnchorHint["direct"] }).direct = direct;
  }
  return hint;
}
